// W2Cook Billing Dashboard — Firebase Auth
//
// IMPORTANT: these values are baked in at BUILD time by Vite.
// They must be present as GitHub Secrets (the GH Actions workflow builds the
// bundle). Setting them only in the Cloudflare Pages dashboard has no effect,
// because Cloudflare receives an already-built dist/.
//
// Required GitHub Secrets:
//   VITE_FIREBASE_API_KEY
//   VITE_FIREBASE_AUTH_DOMAIN
//   VITE_FIREBASE_PROJECT_ID

import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged as fbOnAuthStateChanged,
  type Auth,
  type User,
} from "firebase/auth";

const firebaseConfig = {
  apiKey:     import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId:  import.meta.env.VITE_FIREBASE_PROJECT_ID,
};

// ── Config validation ─────────────────────────────────────────────────────────
// Never throw at module load — a throw here white-screens the whole app with no
// visible error. Instead expose the problem so the UI can render a message.
export const missingConfigKeys: string[] = Object.entries(firebaseConfig)
  .filter(([, v]) => !v)
  .map(([k]) => k);

export const isConfigured = missingConfigKeys.length === 0;

let app: FirebaseApp | null = null;
let authInstance: Auth | null = null;

if (isConfigured) {
  try {
    app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
    authInstance = getAuth(app);
  } catch (e) {
    console.error("Firebase initialisation failed:", e);
    authInstance = null;
  }
}

export const auth = authInstance;

// ── Allowed email domains ─────────────────────────────────────────────────────
const ALLOWED_DOMAINS = ["w2cook.com", "dhivinslabs.com"];

export function isAllowedEmail(email: string): boolean {
  const domain = email.split("@")[1]?.toLowerCase();
  return ALLOWED_DOMAINS.includes(domain ?? "");
}

// ── Auth helpers ──────────────────────────────────────────────────────────────
export async function signIn(email: string, password: string): Promise<User> {
  if (!auth) throw new Error("Authentication is not configured.");
  if (!isAllowedEmail(email)) {
    throw new Error("Access restricted to @w2cook.com and @dhivinslabs.com accounts.");
  }
  const result = await signInWithEmailAndPassword(auth, email, password);
  return result.user;
}

export async function signOut(): Promise<void> {
  if (!auth) return;
  await firebaseSignOut(auth);
}

export function onAuthStateChanged(cb: (user: User | null) => void): () => void {
  if (!auth) { cb(null); return () => {}; }
  return fbOnAuthStateChanged(auth, cb);
}

export type { User };
